import {
  enableProdMode,
  ErrorHandler,
  provideAppInitializer,
  inject,
  provideZoneChangeDetection
} from "@angular/core";

import { environment } from "environments/environment";

import * as Sentry from "@sentry/angular";
import { sentryOptions } from "../../../libs/sentry/sentry.options";
import { TM_APP_CONFIG } from "./app/app.tm.config";
import { provideRouter, Router, Routes, withHashLocation, withInMemoryScrolling } from "@angular/router";
import {
	HTTP_INTERCEPTORS,
	provideHttpClient,
	withInterceptorsFromDi
} from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideState, provideStore } from "@ngrx/store";
import { CustomSerializer, reducers } from "libs/tm-common/src/lib/store/reducers/reducers";
import { metaReducers } from "libs/tm-common/src/lib/store/reducers/metareducers";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { provideEffects } from "@ngrx/effects";
import { RouterEffects } from "libs/tm-common/src/lib/store/effects/effects";
import { AppComponent } from "./app/app.tm.component";
import { CustomerAuthGuard } from "libs/auth/src/lib/services";
import { CustomerAddressesResolver } from "libs/customer/src/lib/services/customer-addresses.resolver";
import { AnamneseDeactivateGuard } from "./app/anamnese/guards/anamnese-deactivate.guard";
import { MediaResolver } from "libs/media/src/lib";
import { MessagesResolver } from "libs/messages/src/lib/services/messages.resolver";
import { PatientsResolver } from "libs/patients/src/lib/services/patients.resolver";
import { ShippingCostResolver } from "libs/sap/src/lib/services/shipping-cost.resolver";
import { PublicDocumentsResolver } from "libs/tm-common/src/lib/services/public-documents.resolver";
import { TreatmentsResolver, TreatmentResolver } from "libs/treatments/src/lib/services";
import {
	provideRouterStore,
	RouterStateSerializer} from "@ngrx/router-store";
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import * as fromAdmin from "libs/admins/src/lib/store/admins.reducer";
import * as fromAnamnese from "libs/anamnese/src/lib/store/anamnese.reducer";
import * as fromAuth from "libs/auth/src/lib/store/auth.reducer";
import * as fromCustomer from "libs/customer/src/lib/store/customer.reducer";
import * as fromMedia from "libs/media/src/lib/store/media.reducer";
import * as fromMessages from "libs/messages/src/lib/store/messages.reducer";
import * as fromOrder from "libs/order/src/lib/store/order.reducer";
import * as fromPatient from "libs/patients/src/lib/store/patient.reducer";
import * as fromTreatment from "libs/treatments/src/lib/store/treatment.reducer";
import * as fromFeatureFlags from "libs/feature-flags/src/lib/store/feature-flags.reducer";
import * as fromMaintenance from "libs/maintenance/src/lib/store/maintenance.reducer";
import { provideNativeDateAdapter } from "@angular/material/core";
import { HttpRequestInterceptor } from "libs/auth/src/lib/services/auth.http.interceptor";
import { OrderEffects } from "libs/order/src/lib";
import { TreatmentEffects } from "libs/treatments/src/lib/store/treatment.effects";
import { AnamneseEffects } from "libs/anamnese/src/lib/store/anamnese.effects";
import { AdminsEffects } from "libs/admins/src/lib/store/admins.effects";
import { CustomerEffects } from "libs/customer/src/lib/store/customer.effects";
import { MediaEffects } from "libs/media/src/lib/store/media.effects";
import { MessagesEffects } from "libs/messages/src/lib/store/messages.effects";
import { PatientEffects } from "libs/patients/src/lib/store/patient.effects";
import { FeatureFlagsEffects } from "libs/feature-flags/src/lib/store/feature-flags.effects";
import { AuthEffects } from "libs/auth/src/lib/store/auth.effects";
import { APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { FeatureFlagsService } from "libs/feature-flags/src/lib/services/feature-flags.lib.service";
import { MaintenanceService } from "libs/maintenance/src/lib/services/maintenance.lib.service";
import {
	webviewerModificationEffects,
	webviewerModificationFeature,
} from "libs/web-viewer/src/lib/store/webviewer-modification.store";
import { ViewportService } from "libs/tm-common/src/lib/services/viewport.lib.service";

const routes: Routes = [
	{ path: "", redirectTo: "/dashboard", pathMatch: "full" },
	// auth routes
	{ path: "signin", loadComponent: () => import('libs/auth/src/lib/signin/signin.component').then(m => m.SigninComponent) },
	{
		path: "register",
		resolve: {
			documents: PublicDocumentsResolver
		},
		loadComponent: () => import('libs/auth/src/lib/registration/registration.component').then(m => m.RegistrationComponent)
	},

	// anamnese routes
	{
		path: "anamnese",
		resolve: {
			patients: PatientsResolver,
			addresses: CustomerAddressesResolver,
			shippingCosts: ShippingCostResolver,
			documents: PublicDocumentsResolver
		},
		loadComponent:() => import('./app/anamnese/anamnese.component').then(m => m.AnamneseComponent),
		canActivate: [CustomerAuthGuard],
		canDeactivate: [AnamneseDeactivateGuard],
	},

	// dashboard routes
	{
		path: "dashboard",
		resolve: {
			patients: PatientsResolver,
			treatments: TreatmentsResolver
		},
		canActivate: [CustomerAuthGuard],
		children: [
			{ path: "", loadComponent: () => import('./app/dashboard/dashboard.tm.component').then(m => m.DashboardComponent) },
			{ path: "patients", loadComponent: () => import('./app/dashboard/dashboard.tm.component').then(m => m.DashboardComponent) }
		]
	},
	// help routes
	{
		path: "help",
		canActivate: [CustomerAuthGuard],
		loadComponent: () => import('./app/help/help.component').then(m => m.HelpComponent)
	},
	// patients routes
	{
		path: "patient",
		canActivate: [CustomerAuthGuard],
		resolve: {
			patients: PatientsResolver,
			treatments: TreatmentsResolver
		},
		children: [
			{ path: "", loadComponent: () => import('./app/patients/patients-initial/patients-initial.component').then(m => m.PatientsInitialComponent), pathMatch: "full" },
			{ path: ":action", loadComponent: () => import('./app/patients/patient/patient.component').then(m => m.PatientComponent) },
			{ path: ":action/:patientId", loadComponent: () => import('./app/patients/patient/patient.component').then(m => m.PatientComponent) }
		]
	},
	// treatments routes
	{
		path: "treatments/edit/:id",
		loadComponent: () => import('./app/treatments/treatment.tm.component').then(m => m.TreatmentComponent),
		canActivate: [CustomerAuthGuard],
		resolve: {
			treatment: TreatmentResolver,
			addresses: CustomerAddressesResolver,
			messages: MessagesResolver,
			medias: MediaResolver
		}
	},
	// promotions routes
	{
		path: "promotions",
		loadComponent: () => import('./app/promotions/promotions.component').then(m => m.PromotionsComponent),
		canActivate: [CustomerAuthGuard]
	},
	// print routes
	{ path: "print", loadComponent: () => import('libs/questionaire/src/lib/questionaire-print/questionaire-print-dialog.component').then(m => m.QuestionairePrintDialogComponent) },
	// error route
	{ path: "error", loadComponent: () => import('libs/ux-components/src/lib/components/error-page/error-page.component').then(m => m.ErrorPageComponent) }
];

if (environment.production) {
	enableProdMode();
	Sentry.init(sentryOptions(TM_APP_CONFIG));
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		provideTranslateService({
			loader: provideTranslateHttpLoader({
				prefix: "./assets/i18n/",
				suffix: `.json`,
				enforceLoading: true,
			})
		}),
		provideRouter(
			routes,
			withHashLocation(),
			withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
		),
		provideRouterStore(),
		provideStore(reducers, {
			metaReducers,
			runtimeChecks: {
				strictStateImmutability: true,
				strictActionImmutability: true
			}
		}),
		provideState("admin", fromAdmin.reducer),
		provideState("anamnese", fromAnamnese.reducer),
		provideState("auth", fromAuth.reducer),
		provideState("customer", fromCustomer.reducer),
		provideState("media", fromMedia.reducer),
		provideState("messages", fromMessages.reducer),
		provideState("order", fromOrder.reducer),
		provideState("featureFlags", fromFeatureFlags.reducer),
		provideState("maintenance", fromMaintenance.reducer),
		provideState("patient", fromPatient.reducer),
		provideState("treatment", fromTreatment.reducer),
		provideState(webviewerModificationFeature),
		provideEffects([
			RouterEffects,
			AdminsEffects,
			AnamneseEffects,
			AuthEffects,
			CustomerEffects,
			MediaEffects,
			MessagesEffects,
			FeatureFlagsEffects,
			OrderEffects,
			PatientEffects,
			TreatmentEffects,
			webviewerModificationEffects
		]),
		provideHttpClient(withInterceptorsFromDi()),
		provideNativeDateAdapter(),
		provideAnimations(),
		provideLoadingBarInterceptor(),
		provideLoadingBarRouter(),
		{
			provide: APP_CONFIG_TOKEN,
			useValue: TM_APP_CONFIG
		},
		provideAppInitializer(() => inject(FeatureFlagsService).loadFeatureFlags()),
		provideAppInitializer(() => inject(ViewportService).initializeViewportObserver()),
		provideAppInitializer(() => {
			const maintenanceService = inject(MaintenanceService);
			maintenanceService.fetchAndStoreMaintenanceTime();
		}),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false
			})
		},
		{
			provide: Sentry.TraceService,
			deps: [Router]
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpRequestInterceptor,
			multi: true
		},
		{
			provide: RouterStateSerializer,
			useClass: CustomSerializer
		},
		...(environment.reduxDevTools
			? [
					provideStoreDevtools({
						maxAge: 25,
						logOnly: false,
						connectInZone: true,
						trace: true
					})
				]
			: [])
	]
}).catch((err) => console.log(err));
